import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, Button, Form, Alert, Badge, Spinner, Accordion, InputGroup, ListGroup, ProgressBar, Modal, Nav, Tab } from 'react-bootstrap';
import axios from 'axios';
import { FaRobot, FaCheckCircle, FaTimesCircle, FaCog, FaCode, FaFileAlt, FaList, FaComments, FaPaperPlane, FaWrench, FaFileUpload, FaGithub, FaDownload, FaTrash, FaMagic, FaStream, FaClone, FaEye, FaPlus, FaSearch, FaEdit, FaExternalLinkAlt, FaRedo, FaPaintBrush } from 'react-icons/fa';
import ConfigEditor from './ConfigEditor';
import DrawingCanvas from './DrawingCanvas';
import EditableAgentMeta from './EditableAgentMeta';
import { API_URL } from '../config';

// Use the centralized API_URL from config
const API = API_URL;

// Available tags for agent classification
const AVAILABLE_TAGS = [
  { value: 'accessibility', label: 'Accessibility', color: 'info' },
  { value: '3D printing', label: '3D Printing', color: 'danger' },
  { value: 'laser cutting', label: 'Laser Cutting', color: 'warning' },
  { value: 'embroidery', label: 'Embroidery', color: 'success' },
  { value: 'knitting', label: 'Knitting', color: 'secondary' },
];

const AgentCreator = forwardRef((props, ref) => {
  // Form state
  const [purpose, setPurpose] = useState('');
  const [expectedInputs, setExpectedInputs] = useState('');
  const [expectedOutputs, setExpectedOutputs] = useState('');
  const [examples, setExamples] = useState('');
  const [agentName, setAgentName] = useState('');
  const [agentType, setAgentType] = useState('ai'); // 'ai' or 'mock'
  
  // Type system state
  const [inputType, setInputType] = useState('string');
  const [inputExtensions, setInputExtensions] = useState('');
  const [outputType, setOutputType] = useState('string');
  const [outputExtensions, setOutputExtensions] = useState('');
  
  // MCP configuration state
  const [showMcpSection, setShowMcpSection] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const [availableMcpServers, setAvailableMcpServers] = useState([]);
  const [loadingMcpServers, setLoadingMcpServers] = useState(false);
  
  // Process state
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  
  // Verification state
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatting, setIsChatting] = useState(false);
  const [currentCode, setCurrentCode] = useState('');
  
  // Sample / reference files state
  const [sampleFiles, setSampleFiles] = useState([]);
  const [uploadingSample, setUploadingSample] = useState(false);
  const pendingNameRef = useRef(`_pending_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`);
  const [pendingSampleIds, setPendingSampleIds] = useState([]);

  // Drawing canvas state
  const [showDrawing, setShowDrawing] = useState(false);
  const [drawingTarget, setDrawingTarget] = useState('input'); // 'input' | 'reference'
  
  // Agent testing state
  const [testInput, setTestInput] = useState('');
  const [testFiles, setTestFiles] = useState([]);
  const [testOutput, setTestOutput] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testError, setTestError] = useState('');
  const [chainAgent, setChainAgent] = useState('');
  const [compatibleAgents, setCompatibleAgents] = useState({ downstream: [], upstream: [] });
  
  // GitHub repository state
  const [githubUrl, setGithubUrl] = useState('');
  const [installedRepos, setInstalledRepos] = useState([]);
  const [isInstallingRepo, setIsInstallingRepo] = useState(false);
  const [repoInstallError, setRepoInstallError] = useState('');
  const [repoInstallSuccess, setRepoInstallSuccess] = useState('');
  const [showGithubSection, setShowGithubSection] = useState(false);
  
  // Prompt refiner state
  const [isRefining, setIsRefining] = useState(false);
  const [refinedPrompt, setRefinedPrompt] = useState(null);
  const [showRefineModal, setShowRefineModal] = useState(false);
  
  // Tags state
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Streaming progress state
  const [useStreaming, setUseStreaming] = useState(true);
  const [streamingSteps, setStreamingSteps] = useState([]);
  const [streamingError, setStreamingError] = useState('');
  const [creationFailure, setCreationFailure] = useState(null); // Detailed failure info from quality gate
  const eventSourceRef = useRef(null);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('create');
  
  // Existing agents state
  const [existingAgents, setExistingAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [agentTagFilter, setAgentTagFilter] = useState(null);
  const [showAgentConfigEditor, setShowAgentConfigEditor] = useState(false);
  const [editingAgentName, setEditingAgentName] = useState(null);
  const [expandedAgentCards, setExpandedAgentCards] = useState({});
  const [repairingAgents, setRepairingAgents] = useState({});
  const agentSamplesCache = useRef({});
  
  // Expose methods for cross-tab communication (e.g., Workflow Wizard → Agent Creator)
  useImperativeHandle(ref, () => ({
    prefillFromWizard({ purpose: wizardPurpose, name: wizardName }) {
      // Switch to the "Create Agent" tab and pre-fill fields
      setActiveTab('create');
      if (wizardPurpose) setPurpose(wizardPurpose);
      if (wizardName) setAgentName(wizardName);
      // Reset any old results
      setResult(null);
      setCreationFailure(null);
      setCurrentCode('');
      setStreamingSteps([]);
      setStreamingError('');
      setError('');
    },
  }));

  // Load installed repos on mount
  useEffect(() => {
    loadInstalledRepos();
    loadAvailableMcpServers();
  }, []);

  // Load available MCP servers from the backend
  const loadAvailableMcpServers = async () => {
    setLoadingMcpServers(true);
    try {
      const response = await axios.get(`${API}/api/smart-agent/mcp-servers`);
      if (response.data.success) {
        setAvailableMcpServers(response.data.servers || []);
      }
    } catch (err) {
      console.error('Failed to load MCP servers:', err);
    } finally {
      setLoadingMcpServers(false);
    }
  };

  // Reset all state for creating a new agent
  const handleReset = () => {
    // Form fields
    setPurpose('');
    setExpectedInputs('');
    setExpectedOutputs('');
    setExamples('');
    setAgentName('');
    setAgentType('ai');
    setInputType('string');
    setInputExtensions('');
    setOutputType('string');
    setOutputExtensions('');
    
    // MCP
    setShowMcpSection(false);
    setMcpServers([]);
    
    // Process/Result state
    setError('');
    setResult(null);
    setVerificationResult(null);
    
    // Chat state
    setShowChat(false);
    setChatMessage('');
    setChatHistory([]);
    setCurrentCode('');
    
    // Sample files
    setSampleFiles([]);
    
    // Test state
    setTestInput('');
    setTestFile(null);
    setTestOutput(null);
    setTestError('');
    
    // GitHub
    setGithubUrl('');
    setRepoInstallError('');
    setRepoInstallSuccess('');
    
    // Prompt refiner
    setRefinedPrompt(null);
    setShowRefineModal(false);
    
    // Tags
    setSelectedTags([]);
    
    // Streaming
    setStreamingSteps([]);
    setStreamingError('');
    setCreationFailure(null);
  };
  
  // === Existing Agents Functions ===
  const loadExistingAgents = async () => {
    setLoadingAgents(true);
    try {
      const response = await axios.get(`${API_URL}/api/load-agents`);
      setExistingAgents(response.data.agents || response.data || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'existing') {
      loadExistingAgents();
    }
  }, [activeTab]);

  // Preview compatible agents as user selects output/input types during creation
  useEffect(() => {
    if (activeTab !== 'create') return;
    const outExts = outputExtensions.split(',').map(s => s.trim()).filter(Boolean);
    const inExts = inputExtensions.split(',').map(s => s.trim()).filter(Boolean);
    const hasOutput = outputType === 'file' && outExts.length > 0;
    const hasInput = inputType === 'file' && inExts.length > 0;
    if (!hasOutput && !hasInput) {
      setCompatibleAgents({ downstream: [], upstream: [] });
      return;
    }
    const timer = setTimeout(() => {
      const outSchema = hasOutput ? { type: 'file', extensions: outExts } : { type: outputType };
      const inSchema = hasInput ? { type: 'file', extensions: inExts } : { type: inputType };
      fetchCompatibleAgents(outSchema, inSchema, agentName || undefined);
    }, 500);
    return () => clearTimeout(timer);
  }, [outputType, outputExtensions, inputType, inputExtensions, activeTab]);

  const handleDuplicateAgent = async (agent) => {
    const newName = prompt(`Enter a name for the duplicate agent:`, `${agent.name}_copy`);
    if (!newName || !newName.trim()) return;
    try {
      setLoadingAgents(true);
      const response = await axios.post(`${API_URL}/api/agents/duplicate`, {
        source_agent: agent.name,
        new_agent_name: newName.trim()
      });
      if (response.data.success) {
        alert(`Agent "${newName.trim()}" duplicated successfully!`);
        await loadExistingAgents();
      } else {
        alert(`Failed to duplicate: ${response.data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error duplicating agent: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleEditAgent = (agentName) => {
    setEditingAgentName(agentName);
    setShowAgentConfigEditor(true);
  };

  const handleUpdateAgentMeta = async (agentName, updates) => {
    try {
      await axios.patch(`${API}/api/manifest/agent-meta/${encodeURIComponent(agentName)}`, updates);
      setExistingAgents(prev => prev.map(a =>
        a.name === agentName ? { ...a, ...updates } : a
      ));
    } catch (err) {
      console.error('Failed to update agent meta:', err);
    }
  };

  const handleRepairAgent = async (agentName) => {
    setRepairingAgents(prev => ({ ...prev, [agentName]: 'loading' }));
    try {
      const response = await axios.post(`${API}/api/manifest/repair-agent/${encodeURIComponent(agentName)}`);
      const d = response.data;
      if (d.success || d.status === 'already_working' || d.status === 'repaired') {
        setRepairingAgents(prev => ({ ...prev, [agentName]: 'success' }));
        setTimeout(() => loadExistingAgents(), 1000);
      } else {
        setRepairingAgents(prev => ({ ...prev, [agentName]: d.status || 'failed' }));
      }
    } catch (err) {
      console.error('Repair failed:', err);
      setRepairingAgents(prev => ({ ...prev, [agentName]: 'failed' }));
    }
  };

  const handleRenameAgent = async (agent) => {
    const newName = window.prompt('Enter new name for this agent:', agent.name);
    if (!newName || newName.trim() === agent.name) return;
    try {
      const response = await axios.post(
        `${API}/api/manifest/rename-agent/${encodeURIComponent(agent.name)}`,
        { new_name: newName.trim() }
      );
      if (response.data.success) {
        await loadExistingAgents();
      } else {
        alert(response.data.error || 'Rename failed');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Rename failed');
    }
  };

  const handleDuplicateToCreate = (agent) => {
    // Pre-fill the create form with the agent's info and switch to create tab
    setPurpose(agent.description || '');
    setAgentName(`${agent.name}_v2`);
    setAgentType(agent.agentType || 'ai');
    if (agent.tags) {
      setSelectedTags(agent.tags);
    }
    setActiveTab('create');
  };

  const filteredExistingAgents = existingAgents.filter(agent => {
    const matchesSearch = !agentSearchQuery || 
      agent.name.toLowerCase().includes(agentSearchQuery.toLowerCase()) ||
      (agent.description || '').toLowerCase().includes(agentSearchQuery.toLowerCase());
    const matchesTag = !agentTagFilter || (agent.tags && agent.tags.includes(agentTagFilter));
    return matchesSearch && matchesTag;
  });

  const toggleAgentCard = useCallback(async (agentName) => {
    setExpandedAgentCards(prev => {
      const next = { ...prev, [agentName]: !prev[agentName] };
      return next;
    });
    if (!agentSamplesCache.current[agentName]) {
      try {
        const res = await axios.get(`${API}/api/agent-samples/${agentName}`);
        agentSamplesCache.current[agentName] = res.data || [];
        setExpandedAgentCards(prev => ({ ...prev })); // trigger re-render
      } catch {
        agentSamplesCache.current[agentName] = [];
      }
    }
  }, []);

  const loadInstalledRepos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/github/installed`);
      if (response.data.success) {
        setInstalledRepos(response.data.repos || []);
      }
    } catch (err) {
      console.error('Failed to load installed repos:', err);
    }
  };
  
  const handleInstallRepo = async () => {
    if (!githubUrl.trim()) {
      setRepoInstallError('Please enter a GitHub URL');
      return;
    }
    
    if (!githubUrl.includes('github.com')) {
      setRepoInstallError('Only GitHub URLs are supported');
      return;
    }
    
    setIsInstallingRepo(true);
    setRepoInstallError('');
    setRepoInstallSuccess('');
    
    try {
      const response = await axios.post(`${API_URL}/api/github/clone-and-install`, {
        github_url: githubUrl,
        force: false
      });
      
      if (response.data.success) {
        setRepoInstallSuccess(`Successfully installed ${response.data.repo_name}! Module name: ${response.data.module_name}`);
        setGithubUrl('');
        await loadInstalledRepos();
      } else {
        setRepoInstallError(response.data.error || 'Installation failed');
      }
    } catch (err) {
      setRepoInstallError(err.response?.data?.error || err.message || 'Installation failed');
    } finally {
      setIsInstallingRepo(false);
    }
  };
  
  const handleRemoveRepo = async (repoName) => {
    if (!window.confirm(`Remove ${repoName}? This will uninstall the package.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_URL}/api/github/repo/${repoName}`);
      if (response.data.success) {
        await loadInstalledRepos();
      } else {
        setRepoInstallError(response.data.error || 'Failed to remove repository');
      }
    } catch (err) {
      setRepoInstallError(err.response?.data?.error || err.message || 'Failed to remove repository');
    }
  };

  // Refine prompt using AI
  const handleRefinePrompt = async () => {
    if (!purpose.trim()) {
      setError('Please enter a purpose first');
      return;
    }
    
    setIsRefining(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/api/smart-agent/refine-prompt`, {
        purpose,
        expected_inputs: expectedInputs,
        expected_outputs: expectedOutputs
      });
      
      if (response.data.success) {
        setRefinedPrompt(response.data);
        setShowRefineModal(true);
      } else {
        setError(response.data.error || 'Failed to refine prompt');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to refine prompt');
    } finally {
      setIsRefining(false);
    }
  };
  
  // Accept refined prompt
  const handleAcceptRefinedPrompt = () => {
    if (refinedPrompt) {
      setPurpose(refinedPrompt.refined_purpose);
      if (refinedPrompt.suggested_name && !agentName) {
        setAgentName(refinedPrompt.suggested_name);
      }
      if (refinedPrompt.agent_type) {
        setAgentType(refinedPrompt.agent_type === 'deterministic' ? 'mock' : 'ai');
      }
      if (refinedPrompt.input_format && !expectedInputs) {
        setExpectedInputs(refinedPrompt.input_format);
      }
      if (refinedPrompt.output_format && !expectedOutputs) {
        setExpectedOutputs(refinedPrompt.output_format);
      }
      // Auto-select tags suggested by AI
      if (refinedPrompt.suggested_tags && refinedPrompt.suggested_tags.length > 0) {
        // Filter to only valid tags
        const validTags = refinedPrompt.suggested_tags.filter(
          t => AVAILABLE_TAGS.some(at => at.value === t)
        );
        setSelectedTags(validTags);
      }
    }
    setShowRefineModal(false);
  };
  
  // Create agent with streaming progress
  const handleCreateStreaming = async () => {
    if (!purpose.trim()) {
      setError('Please provide a purpose for the agent');
      return;
    }
    
    setIsCreating(true);
    setError('');
    setResult(null);
    setCreationFailure(null);
    setStreamingSteps([]);
    setStreamingError('');
    setVerificationResult(null);
    setShowChat(false);
    setChatHistory([]);
    setCurrentCode('');
    
    try {
      // Extract GitHub URLs from purpose text for auto-install
      const githubUrlPattern = /https?:\/\/github\.com\/[\w-]+\/[\w.-]+\/?/gi;
      const detectedGithubUrls = purpose.match(githubUrlPattern) || [];
      const manualRepoUrls = installedRepos
        .map(r => r.url || r.github_url)
        .filter(Boolean);
      const allGithubUrls = [...new Set([...detectedGithubUrls, ...manualRepoUrls])];

      // Use fetch for SSE since axios doesn't support streaming well
      const response = await fetch(`${API}/api/smart-agent/create-streaming`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}`,
        },
        body: JSON.stringify({
          purpose,
          expected_inputs: expectedInputs,
          expected_outputs: expectedOutputs,
          examples,
          agent_name: agentName,
          agent_type: agentType,
          mcp_servers: mcpServers.filter(s => s.name && s.command),
          tags: selectedTags,
          github_urls: allGithubUrls
        })
      });
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('event:')) {
            const eventType = line.substring(6).trim();
            continue;
          }
          if (line.startsWith('data:')) {
            try {
              const data = JSON.parse(line.substring(5).trim());
              
              if (data.step !== undefined) {
                setStreamingSteps(prev => {
                  const existing = prev.find(s => s.step === data.step);
                  if (existing) {
                    return prev.map(s => s.step === data.step ? data : s);
                  }
                  return [...prev, data];
                });
              } else if (data.failure_reasons) {
                // creation_failed event — agent did NOT pass quality checks, NOT saved
                setCreationFailure(data);
                // Still give them the code so they can fix it
                if (data.code) {
                  setCurrentCode(data.code);
                }
                setResult(null);
              } else if (data.success !== undefined) {
                // Complete event - store code, quality, and all metadata
                setCreationFailure(null);
                setResult({
                  success: data.success,
                  agent_name: data.agent_name,
                  filename: data.filename,
                  filepath: data.filepath,
                  dependencies: data.dependencies,
                  code: data.code,
                  quality: data.quality || { grade: data.quality_score },
                  prompt_filename: data.prompt_filename,
                  manifest_action: data.manifest_action,
                });
                if (data.code) {
                  setCurrentCode(data.code);
                }
                // Fetch compatible agents and migrate pending samples after successful streaming creation
                if (data.success && data.agent_name) {
                  migratePendingSamples(data.agent_name);
                  const outSchema = outputType === 'file'
                    ? { type: 'file', extensions: outputExtensions.split(',').map(s => s.trim()).filter(Boolean) }
                    : { type: outputType };
                  const inSchema = inputType === 'file'
                    ? { type: 'file', extensions: inputExtensions.split(',').map(s => s.trim()).filter(Boolean) }
                    : { type: inputType };
                  fetchCompatibleAgents(outSchema, inSchema, data.agent_name);
                }
              } else if (data.error) {
                setStreamingError(data.error);
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Streaming creation failed');
      setStreamingError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  // Resolve the upload target: use agentName if set, otherwise session-scoped pending name
  const getSampleUploadName = useCallback(() => {
    return agentName || pendingNameRef.current;
  }, [agentName]);

  const handleSampleUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingSample(true);
    setError('');

    try {
      const uploadedFiles = [];
      const uploadName = getSampleUploadName();
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('description', `Reference sample for ${agentName || 'new agent'}`);
        
        const response = await axios.post(
          `${API}/api/agent-samples/${uploadName}/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        if (response.data.success) {
          const sample = response.data.sample;
          uploadedFiles.push({
            id: sample.id,
            filename: sample.filename,
            file_type: sample.file_type,
            description: sample.description
          });
          // Track for cleanup if creation is cancelled
          if (!agentName) {
            setPendingSampleIds(prev => [...prev, sample.id]);
          }
        }
      }
      
      setSampleFiles(prev => [...prev, ...uploadedFiles]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to upload sample files');
    } finally {
      setUploadingSample(false);
    }
  };

  const handleRemoveSample = async (sampleId) => {
    try {
      await axios.delete(`${API}/api/agent-samples/${sampleId}`);
      setSampleFiles(prev => prev.filter(f => f.id !== sampleId));
      setPendingSampleIds(prev => prev.filter(id => id !== sampleId));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove sample file');
    }
  };

  // Cleanup pending samples on unmount (best-effort)
  useEffect(() => {
    const pendingName = pendingNameRef.current;
    return () => {
      // Fire-and-forget cleanup if there are pending samples
      const token = localStorage.getItem('jwt_token') || '';
      if (pendingName.startsWith('_pending_')) {
        navigator.sendBeacon?.(
          `${API}/api/agent-samples/cleanup`,
          new Blob([JSON.stringify({ pending_name: pendingName })], { type: 'application/json' })
        );
      }
    };
  }, []);

  // Migrate pending samples to the real agent name after successful creation
  const migratePendingSamples = useCallback(async (finalAgentName) => {
    const pendingName = pendingNameRef.current;
    if (!pendingName.startsWith('_pending_') || pendingSampleIds.length === 0) return;
    if (pendingName === finalAgentName) return;
    try {
      await axios.post(`${API}/api/agent-samples/migrate`, {
        from_name: pendingName,
        to_name: finalAgentName,
      });
      setPendingSampleIds([]);
    } catch (err) {
      console.error('Failed to migrate samples:', err);
    }
  }, [pendingSampleIds]);

  // Handle drawing canvas save
  const handleDrawingSave = useCallback((file) => {
    if (drawingTarget === 'input') {
      setTestFiles(prev => [...prev, file]);
    } else {
      // Upload as reference sample
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', 'Drawn reference sample');
      const uploadName = getSampleUploadName();
      axios.post(
        `${API}/api/agent-samples/${uploadName}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ).then(res => {
        if (res.data.success) {
          const sample = res.data.sample;
          setSampleFiles(prev => [...prev, {
            id: sample.id,
            filename: sample.filename,
            file_type: sample.file_type,
            description: sample.description,
          }]);
          if (!agentName) {
            setPendingSampleIds(prev => [...prev, sample.id]);
          }
        }
      }).catch(err => {
        setError('Failed to save drawing as reference sample: ' + (err.message || ''));
      });
    }
  }, [drawingTarget, agentName, getSampleUploadName]);

  const handleCreate = async () => {
    if (!purpose.trim()) {
      setError('Please provide a purpose for the agent');
      return;
    }

    // Use streaming version if enabled
    if (useStreaming) {
      return handleCreateStreaming();
    }

    setIsCreating(true);
    setError('');
    setResult(null);
    setVerificationResult(null);
    setStreamingSteps([]);
    // Clear previous agent's state to prevent caching issues
    setShowChat(false);
    setChatMessage('');
    setChatHistory([]);
    setCurrentCode('');
    setTestInput('');
    setTestFile(null);
    setTestOutput(null);
    setTestError('');

    try {
      // Extract GitHub URLs from purpose text and merge with manually-installed repos
      const githubUrlPattern = /https?:\/\/github\.com\/[\w-]+\/[\w.-]+\/?/gi;
      const detectedGithubUrls = purpose.match(githubUrlPattern) || [];
      const manualRepoUrls = installedRepos
        .map(r => r.url || r.github_url)
        .filter(Boolean);
      const allGithubUrls = [...new Set([...detectedGithubUrls, ...manualRepoUrls])];

      // Build input/output schemas
      const inputSchema = {
        type: inputType,
        name: 'input',
        description: expectedInputs || 'Input data'
      };
      if (inputType === 'file' && inputExtensions.trim()) {
        inputSchema.extensions = inputExtensions.split(',').map(e => e.trim()).filter(e => e);
      }
      
      const outputSchema = {
        type: outputType,
        name: 'output',
        description: expectedOutputs || 'Output data'
      };
      if (outputType === 'file' && outputExtensions.trim()) {
        outputSchema.extensions = outputExtensions.split(',').map(e => e.trim()).filter(e => e);
      }
      
      const response = await axios.post(`${API}/api/smart-agent/create-complete`, {
        purpose,
        expected_inputs: expectedInputs,
        expected_outputs: expectedOutputs,
        examples,
        agent_name: agentName,
        agent_type: agentType, // 'ai' or 'mock'
        auto_install: true,
        auto_test: true,
        sample_files: sampleFiles.map(f => ({
          id: f.id,
          filename: f.filename,
          file_type: f.file_type
        })),
        github_urls: allGithubUrls,
        // Type system fields
        input_schema: inputSchema,
        output_schema: outputSchema,
        // MCP configuration
        mcp_servers: mcpServers.filter(s => s.name && s.command),
        // Tags
        tags: selectedTags
      });

      setResult(response.data);
      
      if (response.data.code) {
        setCurrentCode(response.data.code);
      }
      
      if (!response.data.success) {
        setError(response.data.error || 'Agent creation failed');
      } else {
        migratePendingSamples(response.data.agent_name);
        if (response.data.compatible_downstream || response.data.compatible_upstream) {
          setCompatibleAgents({
            downstream: response.data.compatible_downstream || [],
            upstream: response.data.compatible_upstream || [],
          });
        } else {
          const outSchema = { type: outputType, extensions: outputExtensions.split(',').map(s => s.trim()).filter(Boolean) };
          const inSchema = { type: inputType, extensions: inputExtensions.split(',').map(s => s.trim()).filter(Boolean) };
          fetchCompatibleAgents(
            outputType === 'file' ? outSchema : { type: outputType },
            inputType === 'file' ? inSchema : { type: inputType },
            response.data.agent_name
          );
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create agent');
    } finally {
      setIsCreating(false);
    }
  };

  const handleChat = async () => {
    if (!chatMessage.trim() || !currentCode) return;

    setIsChatting(true);
    setError('');

    try {
      // Gather all available error context for the AI
      const errorParts = [];
      if (result?.test_result?.error) errorParts.push(`Creation test error: ${result.test_result.error}`);
      if (result?.error) errorParts.push(`Creation error: ${result.error}`);
      if (creationFailure?.quality?.test_result?.error) errorParts.push(`Quality gate test error: ${creationFailure.quality.test_result.error}`);
      if (creationFailure?.failure_reasons?.length) errorParts.push(`Failure reasons: ${creationFailure.failure_reasons.join('; ')}`);
      if (testOutput && !testOutput.success && testOutput.error) errorParts.push(`Test execution error: ${testOutput.error}`);
      if (testOutput?.chain_result && !testOutput.chain_result.success) errorParts.push(`Chain agent "${testOutput.chain_result.agent_name}" failed: ${testOutput.chain_result.error}`);
      const combinedError = errorParts.join('\n') || '';

      const response = await axios.post(`${API}/api/smart-agent/chat`, {
        message: chatMessage,
        code: currentCode,
        agent_name: result?.agent_name || creationFailure?.agent_name || agentName,
        purpose: purpose,
        error: combinedError,
        history: chatHistory
      });

      if (response.data.success) {
        // Update chat history
        setChatHistory(response.data.history);
        
        // Update current code with AI's fix
        if (response.data.code) {
          setCurrentCode(response.data.code);
          
          const base = result || {
            agent_name: creationFailure?.agent_name || agentName,
            success: false,
          };
          setResult({
            ...base,
            code: response.data.code,
            dependencies: response.data.dependencies,
            chat_fix: {
              syntax_valid: response.data.syntax_valid,
              syntax_error: response.data.syntax_error,
              explanation: response.data.explanation
            }
          });
        }
        
        // Clear message input
        setChatMessage('');
      } else {
        setError(response.data.error || 'Chat request failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to chat with AI');
    } finally {
      setIsChatting(false);
    }
  };

  const handleSaveFixedAgent = async () => {
    if (!currentCode) {
      setError('No code to save');
      return;
    }

    // Use agent_name from result, or fall back to user input, or use a default
    const nameToUse = result?.agent_name || agentName || 'FixedAgent';

    setIsCreating(true);
    setError('');

    try {
      // Use the complete creation endpoint to properly install, test, and integrate
      const response = await axios.post(`${API}/api/smart-agent/save-complete`, {
        agent_name: nameToUse,
        code: currentCode,
        purpose: purpose || result?.purpose || 'Fixed agent',
        tags: selectedTags
      });

      if (response.data.success) {
        setResult({
          ...result,
          success: true,
          save_result: response.data,
          code: currentCode,
          agent_name: response.data.agent_name || nameToUse,
          supervisor_reloaded: response.data.supervisor_reloaded
        });
        setShowChat(false); // Hide chat after successful save
        alert('✅ Fixed agent saved and integrated successfully!');
      } else {
        setError(response.data.error || 'Failed to save fixed agent');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save agent');
    } finally {
      setIsCreating(false);
    }
  };

  const handleVerify = async () => {
    if (!result || !result.agent_name) {
      setError('No agent to verify');
      return;
    }

    setIsVerifying(true);
    setError('');
    setVerificationResult(null);

    try {
      // Call verification endpoint that tests:
      // 1. Agent loads from manifest
      // 2. Prompt file exists
      // 3. Agent file exists
      // 4. Supervisor can load it
      const response = await axios.post(`${API}/api/smart-agent/verify`, {
        agent_name: result.agent_name
      });

      setVerificationResult(response.data);
    } catch (err) {
      setVerificationResult({
        success: false,
        error: err.response?.data?.error || err.message || 'Verification failed'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleTestAgent = async () => {
    if (!result || !result.agent_name) {
      setTestError('No agent to test');
      return;
    }

    if (!testInput.trim() && testFiles.length === 0) {
      setTestError('Please enter a test prompt or upload at least one file');
      return;
    }

    setIsTesting(true);
    setTestError('');
    setTestOutput(null);

    try {
      let response;
      
      if (testFiles.length > 0) {
        const formData = new FormData();
        formData.append('agent_name', result.agent_name);
        formData.append('input_text', testInput);
        if (chainAgent) formData.append('chain_agent', chainAgent);
        testFiles.forEach((f) => formData.append('input_files', f));
        
        response = await axios.post(`${API}/api/smart-agent/execute`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        });
      } else {
        response = await axios.post(`${API}/api/smart-agent/execute`, {
          agent_name: result.agent_name,
          input_text: testInput,
          chain_agent: chainAgent || undefined,
        }, {
          timeout: 180000,
        });
      }

      setTestOutput(response.data);
    } catch (err) {
      setTestError(err.response?.data?.error || err.message || 'Test execution failed');
      if (err.response?.data) {
        setTestOutput(err.response.data);
      }
    } finally {
      setIsTesting(false);
    }
  };

  // Fetch compatible agents for the newly created agent
  const fetchCompatibleAgents = async (outputSchema, inputSchema, excludeName) => {
    try {
      const response = await axios.post(`${API}/api/smart-agent/compatible-agents`, {
        output_schema: outputSchema,
        input_schema: inputSchema,
        exclude_name: excludeName,
      });
      if (response.data.success) {
        setCompatibleAgents({
          downstream: response.data.downstream || [],
          upstream: response.data.upstream || [],
        });
      }
    } catch (err) {
      console.error('Failed to fetch compatible agents:', err);
    }
  };

  const renderStepResult = (label, stepResult, icon) => {
    if (!stepResult) return null;
    
    const isSuccess = stepResult.success !== false;
    const Icon = isSuccess ? FaCheckCircle : FaTimesCircle;
    const variant = isSuccess ? 'success' : 'danger';

    return (
      <div className="mb-3 p-3 border rounded">
        <h6>
          {icon} {label} <Badge bg={variant} className="ms-2">{isSuccess ? 'Success' : 'Failed'}</Badge>
        </h6>
        {stepResult.message && <div className="text-muted small mb-2">{stepResult.message}</div>}
        {stepResult.error && <Alert variant="danger" className="mb-0 mt-2">{stepResult.error}</Alert>}
        
        {stepResult.packages && (
          <div className="mt-2">
            <strong>Packages:</strong> {stepResult.packages.join(', ')}
            </div>
          )}
        
        {stepResult.filepath && (
          <div className="mt-2">
            <strong>File:</strong> <code>{stepResult.filepath}</code>
        </div>
        )}
        
        {stepResult.action && (
          <div className="mt-2">
            <strong>Action:</strong> {stepResult.action}
            </div>
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid mt-4">
      <Card>
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h1 className="h4 mb-0"><FaRobot aria-hidden="true" /> Smart Agent Creator</h1>
              <small>Create, view, edit, and duplicate agents</small>
            </div>
            {activeTab === 'create' && (result || currentCode || purpose) && (
              <Button 
                variant="outline-light" 
                size="sm"
                onClick={handleReset}
                disabled={isCreating}
                title="Clear all fields and start fresh"
              >
                🔄 New Agent
              </Button>
            )}
          </div>
          <Nav variant="tabs" className="nav-tabs-header">
            <Nav.Item>
              <Nav.Link 
                active={activeTab === 'create'} 
                onClick={() => setActiveTab('create')}
                className={activeTab === 'create' ? 'text-primary bg-white' : 'text-white'}
                style={{ cursor: 'pointer', border: activeTab === 'create' ? '1px solid #dee2e6' : 'none', borderBottom: activeTab === 'create' ? '1px solid white' : 'none' }}
              >
                <FaPlus className="me-1" /> Create Agent
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                active={activeTab === 'existing'} 
                onClick={() => setActiveTab('existing')}
                className={activeTab === 'existing' ? 'text-primary bg-white' : 'text-white'}
                style={{ cursor: 'pointer', border: activeTab === 'existing' ? '1px solid #dee2e6' : 'none', borderBottom: activeTab === 'existing' ? '1px solid white' : 'none' }}
              >
                <FaEye className="me-1" /> Existing Agents {existingAgents.length > 0 && <Badge bg="light" text="dark" className="ms-1">{existingAgents.length}</Badge>}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        
        <Card.Body>
        {activeTab === 'create' ? (
          <>
          <Form>
            <Form.Group className="mb-3" controlId="agent-purpose">
              <Form.Label><strong>Purpose *</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What should this agent do? E.g., 'Convert images to ASCII art for terminal display'"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                disabled={isCreating}
              />
              <div className="d-flex justify-content-between align-items-center mt-1">
                <Form.Text>A clear description of the agent's purpose</Form.Text>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleRefinePrompt}
                  disabled={isRefining || isCreating || !purpose.trim()}
                  title="Use AI to refine and enhance your purpose description"
                >
                  {isRefining ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Refining...
                    </>
                  ) : (
                    <>
                      <FaMagic className="me-1" />
                      Refine with AI
                    </>
                  )}
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="agent-name">
              <Form.Label><strong>Agent Name</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="E.g., Image_to_ASCII (leave blank for auto-generated name)"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                disabled={isCreating}
              />
              <Form.Text>Optional - AI will generate a name if not provided</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" as="fieldset">
              <Form.Label as="legend" className="h6 fw-normal"><strong>Agent Type</strong></Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  id="agentType-ai"
                  name="agentType"
                  label={
                    <span>
                      <Badge bg="primary" className="me-1">AI</Badge>
                      Smart Agent (uses LLM with tools)
                    </span>
                  }
                  checked={agentType === 'ai'}
                  onChange={() => setAgentType('ai')}
                  disabled={isCreating}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="agentType-mock"
                  name="agentType"
                  label={
                    <span>
                      <Badge bg="success" className="me-1">Mock</Badge>
                      Deterministic (no LLM, fast & predictable)
                    </span>
                  }
                  checked={agentType === 'mock'}
                  onChange={() => setAgentType('mock')}
                  disabled={isCreating}
                />
              </div>
              <Form.Text>
                {agentType === 'ai' 
                  ? 'AI agents use GPT-4 to reason and call tools. Best for complex tasks.'
                  : 'Mock agents run fixed code without LLM. Fast, cheap, predictable.'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" as="fieldset">
              <Form.Label as="legend" className="h6 fw-normal"><strong>Tags</strong></Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {AVAILABLE_TAGS.map(tag => {
                  const isSelected = selectedTags.includes(tag.value);
                  return (
                    <label key={tag.value} className={`native-chip${isCreating ? ' disabled' : ''}`}>
                      <input
                        type="checkbox"
                        className="native-chip-input"
                        checked={isSelected}
                        disabled={isCreating}
                        onChange={() => {
                          setSelectedTags(prev =>
                            prev.includes(tag.value)
                              ? prev.filter(t => t !== tag.value)
                              : [...prev, tag.value]
                          );
                        }}
                      />
                      <span
                        className="native-chip-body"
                        style={{
                          fontSize: '0.85rem',
                          padding: '0.4em 0.8em',
                          background: isSelected ? `var(--bs-${tag.color})` : '#fff',
                          color: isSelected ? '#fff' : '#212529',
                          border: `1px solid ${isSelected ? 'transparent' : '#dee2e6'}`,
                        }}
                      >
                        {tag.label}
                      </span>
                    </label>
                  );
                })}
              </div>
              <Form.Text>
                Select tags that describe this agent's domain. Use "Refine with AI" to auto-suggest tags.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="agent-expected-inputs">
              <Form.Label><strong>Expected Inputs</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What inputs does the agent expect? E.g., 'Image filename (string) or full path to image file'"
                value={expectedInputs}
                onChange={(e) => setExpectedInputs(e.target.value)}
                disabled={isCreating}
              />
              {/* Input Type Selection */}
              <div className="mt-2 d-flex gap-2 align-items-start flex-wrap">
                <Form.Select
                  size="sm"
                  style={{ width: '150px' }}
                  value={inputType}
                  onChange={(e) => setInputType(e.target.value)}
                  disabled={isCreating}
                  aria-label="Input type"
                >
                  <option value="string">String</option>
                  <option value="file">File</option>
                  <option value="number">Number</option>
                  <option value="any">Any</option>
                </Form.Select>
                {inputType === 'file' && (
                  <fieldset className="border-0 p-0 m-0" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '400px' }}>
                    <legend className="visually-hidden">Accepted input file extensions</legend>
                    {[
                      { label: 'Image', exts: ['png','jpg','jpeg','gif','bmp','webp','tiff'] },
                      { label: 'Vector', exts: ['svg'] },
                      { label: 'CAD', exts: ['dxf','dwg'] },
                      { label: '3D', exts: ['stl','obj','3mf','scad','step','iges'] },
                      { label: 'Embroidery', exts: ['jef','pes','dst','exp','vp3','hus'] },
                      { label: 'Knitting', exts: ['ks','k','knitout'] },
                      { label: 'Document', exts: ['pdf'] },
                      { label: 'Data', exts: ['json','csv','xml','txt'] },
                      { label: 'G-code', exts: ['gcode','nc'] },
                    ].map(cat => {
                      const selected = inputExtensions.split(',').map(s => s.trim()).filter(Boolean);
                      return cat.exts.map(ext => {
                        const isActive = selected.includes(ext);
                        return (
                          <label key={ext} className={`native-chip${isCreating ? ' disabled' : ''}`}>
                            <input
                              type="checkbox"
                              className="native-chip-input"
                              checked={isActive}
                              disabled={isCreating}
                              onChange={() => {
                                const current = inputExtensions.split(',').map(s => s.trim()).filter(Boolean);
                                const next = isActive ? current.filter(e => e !== ext) : [...current, ext];
                                setInputExtensions(next.join(','));
                              }}
                            />
                            <span
                              className="native-chip-body"
                              style={{
                                padding: '1px 6px', fontSize: '0.7rem',
                                border: `1px solid ${isActive ? '#0d6efd' : '#dee2e6'}`,
                                background: isActive ? '#0d6efd' : 'transparent',
                                color: isActive ? '#fff' : '#495057',
                              }}
                            >.{ext}</span>
                          </label>
                        );
                      });
                    })}
                  </fieldset>
                )}
                <small className="text-muted">Input type</small>
              </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="agent-expected-outputs">
              <Form.Label><strong>Expected Outputs</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="What should the agent return? E.g., 'Dictionary with ascii_art (string) and dimensions (width, height)'"
                value={expectedOutputs}
                onChange={(e) => setExpectedOutputs(e.target.value)}
                disabled={isCreating}
              />
              {/* Output Type Selection */}
              <div className="mt-2 d-flex gap-2 align-items-start flex-wrap">
                <Form.Select
                  size="sm"
                  style={{ width: '150px' }}
                  value={outputType}
                  onChange={(e) => setOutputType(e.target.value)}
                  disabled={isCreating}
                  aria-label="Output type"
                >
                  <option value="string">String</option>
                  <option value="file">File</option>
                  <option value="number">Number</option>
                  <option value="any">Any</option>
                </Form.Select>
                {outputType === 'file' && (
                  <fieldset className="border-0 p-0 m-0" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '400px' }}>
                    <legend className="visually-hidden">Produced output file extensions</legend>
                    {[
                      { label: 'Image', exts: ['png','jpg','jpeg','gif','bmp','webp','tiff'] },
                      { label: 'Vector', exts: ['svg'] },
                      { label: 'CAD', exts: ['dxf','dwg'] },
                      { label: '3D', exts: ['stl','obj','3mf','scad','step','iges'] },
                      { label: 'Embroidery', exts: ['jef','pes','dst','exp','vp3','hus'] },
                      { label: 'Knitting', exts: ['ks','k','knitout'] },
                      { label: 'Document', exts: ['pdf'] },
                      { label: 'Data', exts: ['json','csv','xml','txt'] },
                      { label: 'G-code', exts: ['gcode','nc'] },
                    ].map(cat => {
                      const selected = outputExtensions.split(',').map(s => s.trim()).filter(Boolean);
                      return cat.exts.map(ext => {
                        const isActive = selected.includes(ext);
                        return (
                          <label key={ext} className={`native-chip${isCreating ? ' disabled' : ''}`}>
                            <input
                              type="checkbox"
                              className="native-chip-input"
                              checked={isActive}
                              disabled={isCreating}
                              onChange={() => {
                                const current = outputExtensions.split(',').map(s => s.trim()).filter(Boolean);
                                const next = isActive ? current.filter(e => e !== ext) : [...current, ext];
                                setOutputExtensions(next.join(','));
                              }}
                            />
                            <span
                              className="native-chip-body"
                              style={{
                                padding: '1px 6px', fontSize: '0.7rem',
                                border: `1px solid ${isActive ? '#198754' : '#dee2e6'}`,
                                background: isActive ? '#198754' : 'transparent',
                                color: isActive ? '#fff' : '#495057',
                              }}
                            >.{ext}</span>
                          </label>
                        );
                      });
                    })}
                  </fieldset>
                )}
                <small className="text-muted">Output type</small>
              </div>
            </Form.Group>

            {/* Preview compatible agents while configuring types */}
            {(compatibleAgents.downstream.length > 0 || compatibleAgents.upstream.length > 0) && !result && (
              <Alert variant="light" className="py-2 px-3 mb-3" style={{ fontSize: '0.85rem', border: '1px solid #e0e0e0' }}>
                <strong>Works with:</strong>
                {compatibleAgents.downstream.length > 0 && (
                  <div className="mt-1">
                    <small className="text-muted">Downstream (accepts your output):</small>{' '}
                    {compatibleAgents.downstream.slice(0, 5).map(a => (
                      <Badge key={a.name} bg={a.compatibility === 'exact' ? 'success' : 'warning'} text={a.compatibility === 'exact' ? 'white' : 'dark'} className="me-1">
                        {a.name}
                      </Badge>
                    ))}
                    {compatibleAgents.downstream.length > 5 && <small className="text-muted">+{compatibleAgents.downstream.length - 5} more</small>}
                  </div>
                )}
                {compatibleAgents.upstream.length > 0 && (
                  <div className="mt-1">
                    <small className="text-muted">Upstream (produces your input):</small>{' '}
                    {compatibleAgents.upstream.slice(0, 5).map(a => (
                      <Badge key={a.name} bg={a.compatibility === 'exact' ? 'info' : 'secondary'} className="me-1">
                        {a.name}
                      </Badge>
                    ))}
                    {compatibleAgents.upstream.length > 5 && <small className="text-muted">+{compatibleAgents.upstream.length - 5} more</small>}
                  </div>
                )}
              </Alert>
            )}

            <Form.Group className="mb-3" controlId="agent-examples">
              <Form.Label><strong>Examples (Optional)</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Example inputs and outputs to guide code generation"
                value={examples}
                onChange={(e) => setExamples(e.target.value)}
                disabled={isCreating}
              />
            </Form.Group>

            {/* Reference Samples Section */}
            <Form.Group className="mb-4">
              <Form.Label><strong>Reference Samples (Optional — persist with agent)</strong></Form.Label>
              <div className="border rounded p-3 bg-light">
                <p className="mb-2 small text-muted">
                  <FaFileUpload className="me-1" />
                  Upload or draw examples that help the agent understand your desired style/format. These files persist with the agent for RAG/few-shot learning.
                </p>
                
                {/* Upload + Draw Buttons */}
                <div className="mb-3 d-flex gap-2 align-items-center">
                  <label htmlFor="sample-file-upload" className="btn btn-outline-primary btn-sm mb-0">
                    <FaFileUpload className="me-2" />
                    {uploadingSample ? 'Uploading...' : 'Upload Samples'}
                  </label>
                  <input
                    id="sample-file-upload"
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.svg,.txt,.json,.dxf,.stl,.pdf,.py,.scad,.jef,.pes,.obj,.gcode"
                    onChange={handleSampleUpload}
                    disabled={uploadingSample || isCreating}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => { setDrawingTarget('reference'); setShowDrawing(true); }}
                    disabled={isCreating}
                  >
                    <FaPaintBrush className="me-1" /> Draw
                  </Button>
                  <span className="small text-muted">Images, SVG, JSON, DXF, STL, SCAD, etc.</span>
                </div>
                
                {/* Uploaded Reference Samples */}
                {sampleFiles.length > 0 && (
                  <div>
                    <strong className="small">Uploaded Samples ({sampleFiles.length}):</strong>
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {sampleFiles.map((file) => {
                        const isVisual = ['image', 'svg'].includes(file.file_type);
                        const uploadName = agentName || pendingNameRef.current;
                        const thumbUrl = isVisual ? `${API}/api/agent-samples/file/${uploadName}/${file.filename}` : null;
                        return (
                          <div key={file.id} className="border rounded p-1 bg-white d-flex align-items-center gap-2" style={{ maxWidth: 200 }}>
                            {thumbUrl ? (
                              <img src={thumbUrl} alt={file.filename} style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 3 }} />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: '#eee', borderRadius: 3, fontSize: '0.75rem' }}>
                                {file.file_type}
                              </div>
                            )}
                            <code className="small text-truncate" style={{ maxWidth: 110 }}>{file.filename}</code>
                            <button
                              type="button"
                              onClick={() => handleRemoveSample(file.id)}
                              disabled={isCreating}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', padding: '0 4px', lineHeight: 1 }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Form.Group>

            {/* GitHub Repository Section */}
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0"><strong>GitHub Dependencies</strong></Form.Label>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowGithubSection(!showGithubSection)}
                  className="p-0"
                >
                  {showGithubSection ? '▼ Hide' : '▶ Show'}
                </Button>
              </div>
              
              {showGithubSection && (
                <div className="border rounded p-3 bg-light">
                  <p className="mb-2 small text-muted">
                    <FaGithub className="me-1" />
                    Install Python packages directly from GitHub repositories. These become available for your agents to import.
                  </p>
                  
                  {repoInstallError && (
                    <Alert variant="danger" dismissible onClose={() => setRepoInstallError('')} className="mb-3">
                      {repoInstallError}
                    </Alert>
                  )}
                  
                  {repoInstallSuccess && (
                    <Alert variant="success" dismissible onClose={() => setRepoInstallSuccess('')} className="mb-3">
                      {repoInstallSuccess}
                    </Alert>
                  )}
                  
                  {/* Install from GitHub */}
                  <InputGroup className="mb-3">
                    <InputGroup.Text><FaGithub /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="https://github.com/user/repo"
                      value={githubUrl}
                      onChange={(e) => setGithubUrl(e.target.value)}
                      disabled={isInstallingRepo || isCreating}
                    />
                    <Button
                      variant="primary"
                      onClick={handleInstallRepo}
                      disabled={isInstallingRepo || !githubUrl.trim() || isCreating}
                    >
                      {isInstallingRepo ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Installing...
                        </>
                      ) : (
                        <>
                          <FaDownload className="me-1" />
                          Install
                        </>
                      )}
                    </Button>
                  </InputGroup>
                  
                  {/* Installed Repos List */}
                  {installedRepos.length > 0 && (
                    <div>
                      <strong className="small">Installed Repositories ({installedRepos.length}):</strong>
                      <ListGroup className="mt-2">
                        {installedRepos.map((repo) => (
                          <ListGroup.Item key={repo.name} className="d-flex justify-content-between align-items-center py-2">
                            <div>
                              <FaGithub className="me-2 text-muted" />
                              <strong>{repo.name}</strong>
                              {repo.module_name && (
                                <code className="ms-2 small text-success">import {repo.module_name}</code>
                              )}
                              {repo.has_setup_py && <Badge bg="info" className="ms-2">setup.py</Badge>}
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveRepo(repo.name)}
                              disabled={isCreating}
                              title="Remove repository"
                            >
                              <FaTrash />
                            </Button>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}
                  
                  {installedRepos.length === 0 && (
                    <div className="text-muted small text-center py-2">
                      No GitHub repositories installed yet.
                    </div>
                  )}
                </div>
              )}
            </Form.Group>

            {/* MCP Server Configuration */}
            <Form.Group className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0"><strong>MCP Servers (Optional)</strong></Form.Label>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowMcpSection(!showMcpSection)}
                  className="p-0"
                >
                  {showMcpSection ? '▼ Hide' : '▶ Show'}
                </Button>
              </div>
              
              {showMcpSection && (
                <div className="border rounded p-3 bg-light">
                  <p className="mb-2 small text-muted">
                    <FaCog className="me-1" />
                    Configure MCP (Model Context Protocol) servers for external tool access. 
                    <a href="https://modelcontextprotocol.io/" target="_blank" rel="noopener noreferrer" className="ms-1">Learn more</a>
                  </p>
                  
                  {/* Add MCP Server */}
                  <div className="mb-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setMcpServers([...mcpServers, {
                          name: '',
                          command: 'npx',
                          args: ['-y', '@modelcontextprotocol/server-filesystem', '/data']
                        }]);
                      }}
                      disabled={isCreating}
                    >
                      + Add MCP Server
                    </Button>
                  </div>
                  
                  {/* MCP Servers List */}
                  {mcpServers.map((server, idx) => (
                    <div key={idx} className="mb-3 p-3 border rounded bg-white">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="small">Server {idx + 1}</strong>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            const updated = [...mcpServers];
                            updated.splice(idx, 1);
                            setMcpServers(updated);
                          }}
                          disabled={isCreating}
                        >
                          Remove
                        </Button>
                      </div>
                      
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Server Name</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g., filesystem"
                          value={server.name}
                          onChange={(e) => {
                            const updated = [...mcpServers];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setMcpServers(updated);
                          }}
                          disabled={isCreating}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Command</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="e.g., npx"
                          value={server.command}
                          onChange={(e) => {
                            const updated = [...mcpServers];
                            updated[idx] = { ...updated[idx], command: e.target.value };
                            setMcpServers(updated);
                          }}
                          disabled={isCreating}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-0">
                        <Form.Label className="small mb-1">Arguments (one per line)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          placeholder={"-y\n@modelcontextprotocol/server-filesystem\n/data"}
                          value={server.args?.join('\n') || ''}
                          onChange={(e) => {
                            const updated = [...mcpServers];
                            updated[idx] = { ...updated[idx], args: e.target.value.split('\n').filter(a => a.trim()) };
                            setMcpServers(updated);
                          }}
                          disabled={isCreating}
                          style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                        />
                      </Form.Group>
                    </div>
                  ))}
                  
                  {mcpServers.length === 0 && (
                    <div className="text-muted small text-center py-2">
                      No MCP servers configured. Add one to give your agent access to external tools.
                    </div>
                  )}
                  
                  {/* Available MCP Servers from Backend */}
                  <div className="mt-3 p-3 border rounded bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong className="small">Available MCP Servers</strong>
                      <Button 
                        variant="link" 
                        size="sm" 
                        onClick={loadAvailableMcpServers}
                        disabled={loadingMcpServers}
                      >
                        {loadingMcpServers ? <Spinner animation="border" size="sm" /> : '🔄 Refresh'}
                      </Button>
                    </div>
                    
                    {availableMcpServers.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {availableMcpServers.map((server, idx) => (
                          <Button
                            key={idx}
                            variant={server.image_built ? "outline-success" : "outline-secondary"}
                            size="sm"
                            onClick={() => {
                              // Add this server to mcpServers if not already added
                              const exists = mcpServers.some(s => s.name === server.name);
                              if (!exists) {
                                setMcpServers([...mcpServers, {
                                  name: server.name,
                                  command: server.command || 'docker',
                                  args: server.args || ['run', '--rm', '-i', server.docker_image]
                                }]);
                              }
                            }}
                            disabled={isCreating || mcpServers.some(s => s.name === server.name)}
                            title={`${server.description}\nTools: ${server.tools?.join(', ') || 'none'}\nDocker: ${server.image_built ? 'Built' : 'Not built'}`}
                          >
                            {server.image_built ? '✅' : '⏳'} {server.name}
                          </Button>
                        ))}
                      </div>
                    ) : loadingMcpServers ? (
                      <div className="text-muted small text-center">Loading available servers...</div>
                    ) : (
                      <div className="text-muted small">No MCP servers found in mcp-servers/ directory</div>
                    )}
                  </div>

                  <div className="mt-2">
                    <details>
                      <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                        External MCP Servers (npm)
                      </summary>
                      <ul className="small mt-2 mb-0">
                        <li><code>@modelcontextprotocol/server-filesystem</code> - File system access</li>
                        <li><code>@modelcontextprotocol/server-postgres</code> - PostgreSQL database</li>
                        <li><code>@modelcontextprotocol/server-fetch</code> - HTTP requests</li>
                        <li><code>@modelcontextprotocol/server-memory</code> - Key-value storage</li>
                      </ul>
                    </details>
                  </div>
                </div>
              )}
            </Form.Group>

            {/* Show Live Progress Toggle */}
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="streaming-toggle"
                label={
                  <span>
                    <FaStream className="me-1" />
                    Show live progress (streaming)
                  </span>
                }
                checked={useStreaming}
                onChange={(e) => setUseStreaming(e.target.checked)}
                disabled={isCreating}
              />
              <Form.Text className="text-muted">
                {useStreaming 
                  ? 'See each step as it happens in real-time' 
                  : 'Wait for the complete result'}
              </Form.Text>
            </Form.Group>

            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleCreate}
              disabled={isCreating || !purpose.trim()}
          className="w-100"
        >
              {isCreating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating Agent...
                </>
              ) : (
                <>
                  <FaRobot className="me-2" />
                  Create Agent
                </>
              )}
        </Button>
      </Form>
      
      {/* Streaming Progress Display */}
      {isCreating && useStreaming && streamingSteps.length > 0 && (
        <Card className="mt-3 border-info">
          <Card.Header className="bg-info text-white">
            <FaStream className="me-2" />
            Creating Agent - Live Progress
          </Card.Header>
          <Card.Body>
            {streamingSteps.map((step, idx) => (
              <div key={step.step || idx} className="mb-2">
                <div className="d-flex align-items-center">
                  {step.status === 'in_progress' ? (
                    <Spinner animation="border" size="sm" className="me-2 text-primary" />
                  ) : step.status === 'complete' ? (
                    <FaCheckCircle className="me-2 text-success" />
                  ) : step.status === 'error' ? (
                    <FaTimesCircle className="me-2 text-danger" />
                  ) : (
                    <span className="me-2">⏳</span>
                  )}
                  <strong>{step.name || `Step ${step.step}`}</strong>
                  {step.status === 'complete' && (
                    <Badge bg="success" className="ms-2">Done</Badge>
                  )}
                  {step.status === 'error' && (
                    <Badge bg="danger" className="ms-2">Error</Badge>
                  )}
                </div>
                {step.description && (
                  <div className={`small ms-4 ${step.status === 'error' ? 'text-danger fw-bold' : 'text-muted'}`}>
                    {step.description}
                  </div>
                )}
                {step.message && (
                  <div className="text-muted small ms-4">{step.message}</div>
                )}
                {step.error && (
                  <Alert variant="danger" className="mt-1 mb-0 py-1 ms-4 small">
                    {step.error}
                  </Alert>
                )}
              </div>
            ))}
            
            {/* Progress bar */}
            <ProgressBar 
              className="mt-3"
              animated
              now={Math.round((streamingSteps.filter(s => s.status === 'complete').length / Math.max(streamingSteps.length, 6)) * 100)}
              label={`${streamingSteps.filter(s => s.status === 'complete').length} / ${Math.max(streamingSteps.length, 6)} steps`}
            />
          </Card.Body>
        </Card>
      )}
      
      {/* ============================================================ */}
      {/* CREATION FAILURE — Prominent full-width error panel          */}
      {/* ============================================================ */}
      {creationFailure && (
        <Card className="mt-4 border-danger" style={{ borderWidth: '2px' }}>
          <Card.Header className="bg-danger text-white d-flex align-items-center">
            <FaTimesCircle className="me-2" size={20} />
            <strong className="fs-5">Agent Creation Failed — Not Saved</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-3">{creationFailure.message}</p>
            
            {/* Failure reasons — each one is a prominent card */}
            <div className="mb-3">
              <h6 className="text-danger mb-2">What went wrong:</h6>
              {(creationFailure.failure_reasons || []).map((reason, idx) => (
                <Alert key={idx} variant="danger" className="d-flex align-items-start mb-2 py-2">
                  <FaTimesCircle className="me-2 mt-1 flex-shrink-0" />
                  <span>{reason}</span>
                </Alert>
              ))}
            </div>

            {/* Quality breakdown if available */}
            {creationFailure.quality && (
              <div className="mb-3 p-3 bg-light border rounded">
                <h6>Quality Breakdown:</h6>
                <div className="d-flex flex-wrap gap-3">
                  <div>
                    <strong>Score:</strong>{' '}
                    <Badge bg={creationFailure.quality.grade >= 6 ? 'warning' : 'danger'}>
                      {creationFailure.quality.grade}/10
                    </Badge>
                  </div>
                  <div>
                    <strong>Test:</strong>{' '}
                    <Badge bg={creationFailure.quality.test_passed ? 'success' : 'danger'}>
                      {creationFailure.quality.test_passed ? 'Passed' : 'Failed'}
                    </Badge>
                  </div>
                  {creationFailure.quality.pattern_issues_found > 0 && (
                    <div>
                      <strong>Pattern Issues:</strong>{' '}
                      <Badge bg="danger">{creationFailure.quality.pattern_issues_found} found</Badge>
                    </div>
                  )}
                  {creationFailure.quality.visual_validation && (
                    <div>
                      <strong>Visual:</strong>{' '}
                      <Badge bg={creationFailure.quality.visual_validation.passed ? 'success' : 'warning'}>
                        {creationFailure.quality.visual_validation.score}/10
                      </Badge>
                    </div>
                  )}
                </div>
                {creationFailure.quality.feedback && (
                  <div className="mt-2 small text-muted">{creationFailure.quality.feedback}</div>
                )}
              </div>
            )}

            {/* Generated code — editable so user can fix it */}
            {creationFailure.code && (
              <Accordion className="mb-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <FaCode className="me-2" /> Review Generated Code (editable)
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form.Control
                      as="textarea"
                      rows={20}
                      value={currentCode}
                      onChange={(e) => setCurrentCode(e.target.value)}
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}

            {/* Action buttons */}
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="primary"
                onClick={() => {
                  // Re-try with the same purpose
                  setCreationFailure(null);
                  handleCreateStreaming();
                }}
              >
                <FaRedo className="me-1" /> Try Again
              </Button>
              {currentCode && (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowChat(true)}
                  >
                    <FaRobot className="me-1" /> Chat with AI to Fix
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const resp = await axios.post(`${API}/api/smart-agent/save-agent-complete`, {
                          agent_name: creationFailure.agent_name || agentName || 'unnamed_agent',
                          code: currentCode,
                          purpose: purpose,
                          tags: selectedTags,
                          force: true,
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (resp.data.success) {
                          setCreationFailure(null);
                          setResult({
                            success: true,
                            agent_name: resp.data.agent_name,
                            filename: resp.data.filename,
                            filepath: resp.data.filepath,
                            code: currentCode,
                            quality: { grade: 'N/A (manual save)', passed: true },
                          });
                        } else {
                          setStreamingError(resp.data.error || 'Save failed — code still has issues');
                        }
                      } catch (err) {
                        setStreamingError(err.response?.data?.error || err.message);
                      }
                    }}
                  >
                    <FaCheckCircle className="me-1" /> Save Anyway (after manual edits)
                  </Button>
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Streaming/network error — still show as alert but more prominent */}
      {streamingError && !creationFailure && (
        <Card className="mt-3 border-warning" style={{ borderWidth: '2px' }}>
          <Card.Body className="d-flex align-items-center">
            <FaTimesCircle className="text-danger me-2" size={18} />
            <div>
              <strong>Error:</strong> {streamingError}
            </div>
            <Button variant="outline-secondary" size="sm" className="ms-auto" onClick={() => setStreamingError('')}>
              Dismiss
            </Button>
          </Card.Body>
        </Card>
      )}

          {/* Results Section */}
          {result && (
            <div className="mt-4">
              <hr />
              <h5 className="mb-3">
                {result.success ? (
                  <><FaCheckCircle className="text-success me-2" />Agent Created Successfully!</>
                ) : (
                  <><FaTimesCircle className="text-danger me-2" />Agent Creation Failed</>
                )}
              </h5>

              {result.success && (
                <>
                  {/* Quality Score Display */}
                  {result.quality && (
                    <Alert variant={result.quality.grade >= 8 ? 'success' : result.quality.grade >= 6 ? 'warning' : 'danger'} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>🎯 Code Quality Score:</strong> {result.quality.grade}/10
                          <Badge bg={result.quality.passed ? 'success' : 'warning'} className="ms-2">
                            {result.quality.verdict}
                          </Badge>
                        </div>
                        {result.quality.refinement_iterations > 0 && (
                          <Badge bg="info">
                            {result.quality.refinement_iterations} refinement{result.quality.refinement_iterations > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      {result.quality.pattern_issues_found > 0 && (
                        <div className="mt-2 small text-warning">
                          ⚠️ {result.quality.pattern_issues_found} potential placeholder pattern(s) detected
                        </div>
                      )}
                    </Alert>
                  )}
                  
                  <Alert variant="success">
                    <strong>Agent Name:</strong> {result.agent_name}<br />
                    <strong>File:</strong> <code>{result.filename}</code>
                    {result.filename && (
                      <a
                        href={`${API}/api/smart-agent/download/${result.filename}`}
                        className="btn btn-sm btn-outline-primary ms-2"
                        download={result.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaDownload className="me-1" /> Download .py
                      </a>
                    )}
                    <br />
                    {result.prompt_filename && (
                      <><strong>Prompt File:</strong> <code>{result.prompt_filename}</code><br /></>
                    )}
                    {result.manifest_action && (
                      <><strong>Manifest:</strong> {result.manifest_action}<br /></>
                    )}
                    {result.supervisor_reloaded !== undefined && (
                      <><strong>Supervisor Reloaded:</strong> {result.supervisor_reloaded ? '✅ Yes' : '⚠️ No'}<br /></>
                    )}
                    {result.supervisor_reloaded && (
                      <div className="mt-2 p-2 bg-light border rounded">
                        <small>🔄 Supervisor has been reloaded - your new agent is immediately available in workflows!</small>
                </div>
              )}
                  </Alert>

                  <Accordion defaultActiveKey={["1"]} alwaysOpen className="mb-3">
                    {/* Quality Details */}
                    {result.quality && (
                      <Accordion.Item eventKey="quality">
                        <Accordion.Header>
                          <FaCheckCircle className="me-2" />
                          Quality Report
                        </Accordion.Header>
                        <Accordion.Body>
                          {/* Test Results */}
                          {result.quality.test_passed !== undefined && (
                            <div className="mb-3">
                              <strong>Agent Testing:</strong>
                              <Alert variant={result.quality.test_passed ? 'success' : 'danger'} className="mt-2 mb-0">
                                {result.quality.test_passed ? (
                                  <>
                                    <strong>✅ Test Passed</strong>
                                    <div className="small mt-1">Agent can be instantiated and runs without errors</div>
                                  </>
                                ) : (
                                  <>
                                    <strong>❌ Test Failed</strong>
                                    <div className="small mt-1">{result.quality.test_result?.error || 'Unknown error'}</div>
                                    {result.quality.test_result?.traceback && (
                                      <details className="mt-2">
                                        <summary className="small" style={{cursor: 'pointer'}}>Show traceback</summary>
                                        <pre className="small mt-1 mb-0" style={{fontSize: '0.75em'}}>
                                          {result.quality.test_result.traceback}
                                        </pre>
                                      </details>
                                    )}
                                  </>
                                )}
                              </Alert>
                            </div>
                          )}
                          
                          <div className="mb-3">
                            <strong>Grade Breakdown:</strong>
                            <pre className="mt-2 p-2 bg-light border rounded" style={{fontSize: '0.85em', whiteSpace: 'pre-wrap'}}>
                              {result.quality.feedback}
                            </pre>
                          </div>
                          
                          {result.quality.refinement_history && result.quality.refinement_history.length > 0 && (
                            <div className="mb-3">
                              <strong>Refinement History:</strong>
                              {result.quality.refinement_history.map((ref, idx) => (
                                <div key={idx} className="mt-2 p-2 bg-light border-start border-3 border-info">
                                  <Badge bg="secondary">Iteration {ref.iteration}</Badge>
                                  {ref.grade && <span className="ms-2">Grade: {ref.grade}/10</span>}
                                  {ref.pattern_issues > 0 && (
                                    <Badge bg="warning" className="ms-2">{ref.pattern_issues} issues</Badge>
                                  )}
                                  {ref.test_passed !== undefined && (
                                    <Badge bg={ref.test_passed ? 'success' : 'danger'} className="ms-2">
                                      {ref.test_passed ? 'Test ✓' : 'Test ✗'}
                                    </Badge>
                                  )}
                                  {ref.critique && (
                                    <div className="mt-1 small text-muted">{ref.critique}</div>
                                  )}
                                  {ref.error && (
                                    <div className="mt-1 small text-danger">{ref.error}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {result.quality.pattern_issues && result.quality.pattern_issues.length > 0 && (
                            <div>
                              <strong>Detected Issues:</strong>
                              {result.quality.pattern_issues.map((issue, idx) => (
                                <div key={idx} className="mt-2 p-2 bg-warning-subtle border rounded">
                                  <Badge bg="warning">Line {issue.line}</Badge>
                                  <div className="mt-1 small">{issue.pattern}</div>
                                  <code className="small">{issue.code}</code>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Visual Validation (from creation pipeline) */}
                          {result.quality.visual_validation && result.quality.visual_validation.file_results && result.quality.visual_validation.file_results.length > 0 && (
                            <div className="mt-3">
                              <strong>
                                {result.quality.visual_validation.passed ? '🖼️ ✅' : '🖼️ ⚠️'} Visual Output Validation
                              </strong>
                              <Badge 
                                bg={result.quality.visual_validation.score >= 8 ? 'success' : result.quality.visual_validation.score >= 6 ? 'warning' : 'danger'} 
                                className="ms-2"
                              >
                                {result.quality.visual_validation.score}/10
                              </Badge>
                              <p className="text-muted small mt-1 mb-2">
                                {result.quality.visual_validation.overall_feedback}
                              </p>
                              {result.quality.visual_validation.file_results.map((fr, idx) => (
                                <div key={idx} className="mt-2 p-2 border rounded">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span>
                                      {fr.passed ? <FaCheckCircle className="text-success me-1" /> : <FaTimesCircle className="text-danger me-1" />}
                                      <code className="small">{fr.filename}</code>
                                    </span>
                                    <Badge bg={fr.score >= 8 ? 'success' : fr.score >= 6 ? 'warning' : 'danger'} style={{fontSize: '0.75em'}}>
                                      {fr.score}/10
                                    </Badge>
                                  </div>
                                  {fr.issues && fr.issues.length > 0 && (
                                    <ul className="mb-0 small text-muted ps-3 mt-1">
                                      {fr.issues.map((issue, iidx) => <li key={iidx}>{issue}</li>)}
                                    </ul>
                                  )}
                                  <details className="mt-1">
                                    <summary className="small text-muted" style={{cursor: 'pointer'}}>Full AI Review</summary>
                                    <pre className="mt-1 small p-2 bg-light rounded" style={{fontSize: '0.75em', whiteSpace: 'pre-wrap'}}>{fr.feedback}</pre>
                                  </details>
                                </div>
                              ))}
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    )}
                    
                    <Accordion.Item eventKey="0">
                      <Accordion.Header><FaCog /> Creation Steps</Accordion.Header>
                      <Accordion.Body>
                        {/* Show streaming steps if available (streaming mode) */}
                        {streamingSteps.length > 0 ? (
                          <>
                            {streamingSteps.map((step, idx) => (
                              <div key={step.step || idx} className="mb-2 p-2 border rounded">
                                <div className="d-flex align-items-center">
                                  {step.status === 'complete' ? (
                                    <FaCheckCircle className="me-2 text-success" />
                                  ) : step.status === 'error' ? (
                                    <FaTimesCircle className="me-2 text-danger" />
                                  ) : step.status === 'warning' ? (
                                    <span className="me-2">⚠️</span>
                                  ) : (
                                    <span className="me-2">⏳</span>
                                  )}
                                  <strong className="small">{step.name || `Step ${step.step}`}</strong>
                                  <Badge bg={step.status === 'complete' ? 'success' : step.status === 'error' ? 'danger' : 'secondary'} className="ms-2" style={{fontSize: '0.7em'}}>
                                    {step.status}
                                  </Badge>
                                </div>
                                {step.description && (
                                  <div className="text-muted small ms-4 mt-1">{step.description}</div>
                                )}
                              </div>
                            ))}
                          </>
                        ) : (
                          /* Fallback for non-streaming mode */
                          <>
                            {renderStepResult('1. Code Generation', { success: true, message: `Generated ${result.code?.length || 0} characters of code` }, '🤖')}
                            {renderStepResult('2. Dependency Installation', result.dependency_install, '📦')}
                            {renderStepResult('3. Prompt File Created', result.prompt_result, '📝')}
                            {renderStepResult('4. Agent Testing', result.test_result, '🧪')}
                            {renderStepResult('5. Agent File Saved', result.save_result, '💾')}
                            {renderStepResult('6. Manifest Updated', result.manifest_result, '📋')}
                          </>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>

                    <Accordion.Item eventKey="1">
                      <Accordion.Header><FaCode /> Generated Code</Accordion.Header>
                      <Accordion.Body>
                        <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                          <code>{result.code}</code>
                        </pre>
                      </Accordion.Body>
                    </Accordion.Item>

                    {result.dependencies && result.dependencies.length > 0 && (
                      <Accordion.Item eventKey="2">
                        <Accordion.Header><FaList /> Dependencies</Accordion.Header>
                        <Accordion.Body>
                          <ul className="mb-0">
                            {result.dependencies.map((dep, idx) => (
                              <li key={idx}><code>{dep}</code></li>
                            ))}
                          </ul>
                        </Accordion.Body>
                      </Accordion.Item>
                    )}
                  </Accordion>

                  <div className="d-grid gap-2">
                    <Button 
                      variant="outline-primary"
                      onClick={handleVerify}
                      disabled={isVerifying}
                    >
                      {isVerifying ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Verifying Integration...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="me-2" />
                          Verify Agent Integration
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      variant="outline-info"
                      onClick={() => setShowChat(!showChat)}
                    >
                      <FaComments className="me-2" />
                      {showChat ? 'Hide Chat' : 'Chat with AI to Fix Agent'}
                    </Button>
                  </div>
                </>
              )}

              {result.error && (
                <Alert variant="danger">
                  <strong>Error:</strong> {result.error}
                  {result.line && <div className="mt-1"><code>Line {result.line}</code></div>}
                </Alert>
              )}

              {result.warning && (
                <Alert variant="warning">
                  <strong>Warning:</strong> {result.warning}
                </Alert>
              )}
              
              {/* Show generated code even if there was an error (for debugging) */}
              {!result.success && result.code && (
                <Accordion className="mb-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header><FaCode /> Generated Code (with errors)</Accordion.Header>
                    <Accordion.Body>
                      <Alert variant="info" className="mb-2">
                        <small>
                          This code has syntax errors. Use the chat below to ask the AI to fix them.
                        </small>
                      </Alert>
                      <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                        <code>{result.code}</code>
                      </pre>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              )}
              
              {/* Chat button - show whenever we have code (success or failure) */}
              {currentCode && (
                <div className="d-grid gap-2 mt-3">
                  <Button 
                    variant={result.success ? "outline-info" : "warning"}
                    onClick={() => setShowChat(!showChat)}
                  >
                    <FaComments className="me-2" />
                    {showChat ? 'Hide Chat' : result.success ? 'Chat with AI to Fix Agent' : 'Chat with AI to Fix Errors'}
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Chat Interface */}
          {(result || creationFailure) && currentCode && showChat && (
            <div className="mt-4">
              <Card border="info">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0"><FaComments /> Chat with AI to Fix Agent</h5>
                  <small>Describe the issue and the AI will help fix the code iteratively</small>
                </Card.Header>
                <Card.Body>
                  {result?.chat_fix && (
                    <Alert variant={result.chat_fix.syntax_valid ? 'success' : 'warning'}>
                      <strong>Latest Fix:</strong>
                      <div className="mt-2">
                        {result.chat_fix.syntax_valid ? (
                          <Badge bg="success">✅ Syntax Valid</Badge>
                        ) : (
                          <>
                            <Badge bg="danger">❌ Syntax Error</Badge>
                            <div className="mt-1 small">{result.chat_fix.syntax_error}</div>
                          </>
                        )}
                      </div>
                      <div className="mt-2 small" style={{whiteSpace: 'pre-wrap'}}>{result.chat_fix.explanation}</div>
                    </Alert>
                  )}
                  
                  {/* Chat History */}
                  {chatHistory.length > 0 && (
                    <div className="mb-3 p-3 border rounded" style={{maxHeight: '300px', overflowY: 'auto'}}>
                      {chatHistory.map((msg, idx) => (
                        <div key={idx} className={`mb-2 ${msg.role === 'user' ? 'text-end' : ''}`}>
                          <Badge bg={msg.role === 'user' ? 'primary' : 'secondary'} className="mb-1">
                            {msg.role === 'user' ? 'You' : 'AI'}
                          </Badge>
                          <div className={`p-2 rounded ${msg.role === 'user' ? 'bg-light' : 'bg-white border'}`}>
                            <small style={{whiteSpace: 'pre-wrap'}}>{msg.content}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Chat Input */}
                  <InputGroup>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Describe the issue... e.g., 'The STL file is empty, can you fix the implementation?'"
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleChat();
                        }
                      }}
                      disabled={isChatting}
                    />
                    <Button 
                      variant="info" 
                      onClick={handleChat}
                      disabled={isChatting || !chatMessage.trim()}
                    >
                      {isChatting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <FaPaperPlane />
                      )}
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Press Enter to send, Shift+Enter for new line
                  </Form.Text>
                  
                  {/* Action Buttons */}
                  <div className="d-grid gap-2 mt-3">
                    <Button 
                      variant="success" 
                      onClick={handleSaveFixedAgent}
                      disabled={isCreating || !currentCode}
                    >
                      {isCreating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaWrench className="me-2" />
                          Save Fixed Agent
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
                  
          {/* Verification Results */}
          {verificationResult && (
            <div className="mt-4">
              <hr />
              <h5 className="mb-3">
                {verificationResult.success ? (
                  <><FaCheckCircle className="text-success me-2" />Integration Verified!</>
                ) : (
                  <><FaTimesCircle className="text-danger me-2" />Verification Failed</>
                )}
              </h5>

              {verificationResult.success ? (
                <Alert variant="success">
                  <h6>✅ Agent is fully integrated:</h6>
                  <ul className="mb-0">
                    <li>✅ Agent file exists and is valid</li>
                    <li>✅ Prompt file created</li>
                    <li>✅ Registered in agents_manifest.json</li>
                    <li>✅ Supervisor can load the agent</li>
                    <li>✅ Agent is ready to use in workflows</li>
                  </ul>
                  {verificationResult.agent_count && (
                    <div className="mt-2">
                      <strong>Total agents loaded:</strong> {verificationResult.agent_count}
                    </div>
                  )}
            </Alert>
              ) : (
                <Alert variant="danger">
                  <strong>Verification Error:</strong> {verificationResult.error}
          </Alert>
              )}
            </div>
          )}

          {/* Test Agent Section - appears after successful creation */}
          {result && result.success && (
            <Card className="mt-4" border="success">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">🧪 Test Your Agent</h5>
                <small>Run your agent with custom input to verify it works as expected</small>
              </Card.Header>
              <Card.Body>
                {testError && (
                  <Alert variant="danger" dismissible onClose={() => setTestError('')}>
                    {testError}
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label><strong>Test Prompt / Input Text</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter a test prompt for your agent... e.g., 'Count the words in: Hello world this is a test'"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    disabled={isTesting}
                  />
                  <Form.Text>Enter the text input you want to send to the agent</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Input Files (Optional)</strong></Form.Label>
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="file"
                      multiple
                      onChange={(e) => {
                        const picked = Array.from(e.target.files);
                        if (picked.length > 0) {
                          setTestFiles(prev => {
                            const existing = new Set(prev.map(f => f.name));
                            const newFiles = picked.filter(f => !existing.has(f.name));
                            return [...prev, ...newFiles];
                          });
                          e.target.value = '';
                        }
                      }}
                      disabled={isTesting}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => { setDrawingTarget('input'); setShowDrawing(true); }}
                      disabled={isTesting}
                      title="Draw an input image"
                    >
                      <FaPaintBrush className="me-1" /> Draw
                    </Button>
                  </div>
                  <Form.Text>Upload or draw files to process (image, SVG, STL, text, etc.)</Form.Text>
                  {testFiles.length > 0 && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {testFiles.map((f, i) => (
                        <span key={i} className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded"
                          style={{ background: '#e8f4fd', fontSize: '0.85rem' }}>
                          📎 {f.name}
                          <button
                            type="button"
                            onClick={() => setTestFiles(prev => prev.filter((_, idx) => idx !== i))}
                            disabled={isTesting}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', padding: '0 2px', lineHeight: 1 }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <Button
                        variant="link"
                        size="sm"
                        className="text-muted p-0"
                        onClick={() => setTestFiles([])}
                        disabled={isTesting}
                      >
                        Clear all
                      </Button>
                    </div>
                  )}
                </Form.Group>

                {/* Compatible downstream agents for chained testing */}
                {compatibleAgents.downstream.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>🔗 Chain with downstream agent</strong>
                      <small className="text-muted ms-2">
                        (run a compatible agent on the output)
                      </small>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={chainAgent}
                      onChange={(e) => setChainAgent(e.target.value)}
                      disabled={isTesting}
                    >
                      <option value="">No chain — test this agent only</option>
                      {compatibleAgents.downstream.map((a) => {
                        const exts = a.input_schema?.extensions;
                        const label = exts ? `.${exts.join(', .')}` : a.input_schema?.type || '';
                        return (
                          <option key={a.name} value={a.name}>
                            {a.compatibility === 'exact' ? '✅' : '⚠️'} {a.name} — {a.description?.slice(0, 60) || ''}
                            {label ? ` (accepts ${label})` : ''}
                          </option>
                        );
                      })}
                    </Form.Select>
                    {chainAgent && (
                      <Form.Text className="text-success">
                        Output from your agent → <strong>{chainAgent}</strong>
                      </Form.Text>
                    )}
                  </Form.Group>
                )}

                {/* Compatible upstream agents (can produce input for this agent) */}
                {compatibleAgents.upstream.length > 0 && !compatibleAgents.downstream.length && (
                  <Alert variant="info" className="py-2" style={{ fontSize: '0.85rem' }}>
                    <strong>💡 Tip:</strong> These agents can produce input for your agent:{' '}
                    {compatibleAgents.upstream.slice(0, 5).map(a => a.name).join(', ')}
                  </Alert>
                )}

                <Button
                  variant="success"
                  onClick={handleTestAgent}
                  disabled={isTesting || (!testInput.trim() && testFiles.length === 0)}
                  className="w-100"
                >
                  {isTesting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {chainAgent ? `Running ${result.agent_name} → ${chainAgent}...` : 'Running Agent...'}
                    </>
                  ) : (
                    <>
                      <FaRobot className="me-2" />
                      {chainAgent ? `Run Test → Chain with ${chainAgent}` : 'Run Test'}
                    </>
                  )}
                </Button>

                {/* Test Output Display */}
                {testOutput && (
                  <div className="mt-4">
                    <h6>
                      {testOutput.success ? (
                        <><FaCheckCircle className="text-success me-2" />Test Result</>
                      ) : (
                        <><FaTimesCircle className="text-danger me-2" />Test Failed</>
                      )}
                    </h6>
                    
                    {testOutput.success ? (
                      <>
                        {/* Text Output Display - Always show */}
                        <Card className="mb-3">
                          <Card.Header className="bg-light">
                            <strong>📝 Agent Output</strong>
                            {testOutput.agent_type && (
                              <span className="ms-2 badge bg-secondary">
                                {testOutput.agent_type === 'deterministic' ? 'Mock Agent' : 'AI Agent'}
                              </span>
                            )}
                          </Card.Header>
                          <Card.Body>
                            <pre 
                              className="bg-light p-3 rounded mb-0" 
                              style={{ 
                                maxHeight: '300px', 
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}
                            >
                              {(() => {
                                // Extract text output from various formats
                                const output = testOutput.output || testOutput.result;
                                if (!output) return 'No text output';
                                if (typeof output === 'string') return output;
                                if (typeof output === 'object') {
                                  // Try to get meaningful text from object
                                  if (output.content) return typeof output.content === 'string' ? output.content : JSON.stringify(output.content, null, 2);
                                  if (output.result) return typeof output.result === 'string' ? output.result : JSON.stringify(output.result, null, 2);
                                  if (output.text) return output.text;
                                  if (output.message) return output.message;
                                  // Fallback to pretty JSON
                                  return JSON.stringify(output, null, 2);
                                }
                                return String(output);
                              })()}
                            </pre>
                          </Card.Body>
                        </Card>

                        {/* Generated Files */}
                        {testOutput.generated_files && testOutput.generated_files.length > 0 && (
                          <Card className="mb-3">
                            <Card.Header className="bg-light">
                              <strong>📁 Generated Files ({testOutput.generated_files.length})</strong>
                            </Card.Header>
                            <Card.Body>
                              {testOutput.generated_files.map((file, idx) => {
                                const fname = typeof file === 'string' ? file : (file.filename || '');
                                const filePath = typeof file === 'object' ? (file.file_path || `/generated-files/${fname}`) : `/generated-files/${file}`;
                                const downloadUrl = `${API}/api${filePath.startsWith('/') ? '' : '/'}${filePath}`;
                                const ext = fname.split('.').pop()?.toLowerCase() || '';
                                const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
                                const isSvg = ext === 'svg';
                                const description = typeof file === 'object' ? file.description : '';

                                return (
                                  <div key={idx} className="d-flex align-items-start gap-3 mb-3 p-2 border rounded">
                                    {/* Thumbnail preview for images */}
                                    {(isImage || isSvg) && (
                                      <div style={{ flexShrink: 0, width: 80, height: 80, border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                                        <img
                                          src={downloadUrl}
                                          alt={fname}
                                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      </div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div className="d-flex align-items-center gap-2 mb-1">
                                        <code style={{ fontSize: '0.85em' }}>{fname}</code>
                                        <Badge bg="secondary" style={{ fontSize: '0.7em' }}>.{ext}</Badge>
                                      </div>
                                      {description && (
                                        <div className="text-muted small mb-1">{description}</div>
                                      )}
                                      <div className="d-flex gap-2">
                                        <a
                                          href={downloadUrl}
                                          download={fname}
                                          className="btn btn-sm btn-outline-primary"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <FaDownload className="me-1" /> Download
                                        </a>
                                        {(isImage || isSvg) && (
                                          <a
                                            href={downloadUrl}
                                            className="btn btn-sm btn-outline-secondary"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <FaExternalLinkAlt className="me-1" /> Open
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </Card.Body>
                          </Card>
                        )}

                        {/* Visual Validation Report */}
                        {testOutput.visual_validation && (
                          <Card className="mb-3" border={testOutput.visual_validation.passed ? 'success' : 'warning'}>
                            <Card.Header className={testOutput.visual_validation.passed ? 'bg-success text-white' : 'bg-warning'}>
                              <div className="d-flex justify-content-between align-items-center">
                                <strong>
                                  {testOutput.visual_validation.passed ? '✅' : '⚠️'} AI Visual Validation
                                </strong>
                                <Badge bg={testOutput.visual_validation.score >= 8 ? 'success' : testOutput.visual_validation.score >= 6 ? 'warning' : 'danger'}>
                                  Score: {testOutput.visual_validation.score}/10
                                </Badge>
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <p className="text-muted small mb-2">
                                {testOutput.visual_validation.overall_feedback}
                              </p>
                              
                              {/* Per-file results */}
                              {testOutput.visual_validation.file_results && testOutput.visual_validation.file_results.map((fr, idx) => (
                                <div key={idx} className="mb-2 p-2 border rounded">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div>
                                      {fr.passed ? <FaCheckCircle className="text-success me-1" /> : <FaTimesCircle className="text-danger me-1" />}
                                      <code className="small">{fr.filename}</code>
                                    </div>
                                    <Badge bg={fr.score >= 8 ? 'success' : fr.score >= 6 ? 'warning' : 'danger'} style={{fontSize: '0.75em'}}>
                                      {fr.score}/10
                                    </Badge>
                                  </div>
                                  {fr.issues && fr.issues.length > 0 && (
                                    <ul className="mb-0 small text-muted ps-3">
                                      {fr.issues.map((issue, iidx) => (
                                        <li key={iidx}>{issue}</li>
                                      ))}
                                    </ul>
                                  )}
                                  <details className="mt-1">
                                    <summary className="small text-muted" style={{cursor: 'pointer'}}>Full Review</summary>
                                    <pre className="mt-1 small p-2 bg-light rounded" style={{fontSize: '0.75em', whiteSpace: 'pre-wrap'}}>
                                      {fr.feedback}
                                    </pre>
                                  </details>
                                </div>
                              ))}
                              
                              {/* Overall issues */}
                              {testOutput.visual_validation.issues && testOutput.visual_validation.issues.length > 0 && (
                                <Alert variant="warning" className="mt-2 mb-0 py-2">
                                  <strong className="small">Issues Found:</strong>
                                  <ul className="mb-0 small">
                                    {testOutput.visual_validation.issues.map((issue, idx) => (
                                      <li key={idx}>{issue}</li>
                                    ))}
                                  </ul>
                                </Alert>
                              )}
                            </Card.Body>
                          </Card>
                        )}

                        {/* Chained Agent Result */}
                        {testOutput.chain_result && (
                          <Card className="mb-3" border={testOutput.chain_result.success ? 'info' : 'danger'}>
                            <Card.Header className={testOutput.chain_result.success ? 'bg-info text-white' : 'bg-danger text-white'}>
                              <strong>🔗 Chain Result: {testOutput.chain_result.agent_name}</strong>
                            </Card.Header>
                            <Card.Body>
                              {testOutput.chain_result.success ? (
                                <>
                                  {testOutput.chain_result.output && (
                                    <pre className="bg-light p-3 rounded mb-3" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {testOutput.chain_result.output}
                                    </pre>
                                  )}
                                  {testOutput.chain_result.generated_files && testOutput.chain_result.generated_files.length > 0 && (
                                    <div>
                                      <strong className="small">📁 Files from {testOutput.chain_result.agent_name} ({testOutput.chain_result.generated_files.length})</strong>
                                      {testOutput.chain_result.generated_files.map((file, idx) => {
                                        const fname = typeof file === 'string' ? file : (file.filename || '');
                                        const filePath = typeof file === 'object' ? (file.file_path || `/generated-files/${fname}`) : `/generated-files/${file}`;
                                        const downloadUrl = `${API}/api${filePath.startsWith('/') ? '' : '/'}${filePath}`;
                                        const ext = fname.split('.').pop()?.toLowerCase() || '';
                                        const isVisual = ['png','jpg','jpeg','gif','webp','bmp','svg'].includes(ext);
                                        return (
                                          <div key={idx} className="d-flex align-items-center gap-2 mt-2 p-2 border rounded">
                                            {isVisual && (
                                              <div style={{ width: 60, height: 60, border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa', flexShrink: 0 }}>
                                                <img src={downloadUrl} alt={fname} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display='none'; }} />
                                              </div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                              <code style={{ fontSize: '0.82em' }}>{fname}</code>
                                              <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65em' }}>.{ext}</Badge>
                                            </div>
                                            <a href={downloadUrl} download={fname} className="btn btn-sm btn-outline-primary">
                                              <FaDownload />
                                            </a>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Alert variant="danger" className="mb-2">
                                    Chain failed: {testOutput.chain_result.error}
                                  </Alert>
                                  <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={handleTestAgent}
                                      disabled={isTesting}
                                    >
                                      <FaRedo className="me-1" /> Retry Chain
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => { setChainAgent(''); setTestOutput(null); }}
                                    >
                                      Try Different Agent
                                    </Button>
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => setShowChat(true)}
                                    >
                                      <FaComments className="me-1" /> Chat with AI to Fix
                                    </Button>
                                  </div>
                                </>
                              )}
                            </Card.Body>
                          </Card>
                        )}

                        {/* Raw Result (collapsed by default) */}
                        <details className="mt-2">
                          <summary className="text-muted" style={{cursor: 'pointer'}}>
                            Show Raw Response
                          </summary>
                          <pre 
                            className="bg-dark text-light p-3 rounded mt-2" 
                            style={{ 
                              maxHeight: '200px', 
                              overflow: 'auto',
                              fontSize: '0.8em'
                            }}
                          >
                            {JSON.stringify(testOutput.raw_result, null, 2)}
                          </pre>
                        </details>
                      </>
                    ) : (
                      <Alert variant="danger">
                        <strong>Error:</strong> {testOutput.error}
                        {testOutput.traceback && (
                          <details className="mt-2">
                            <summary style={{cursor: 'pointer'}}>Show Traceback</summary>
                            <pre className="mt-2 small" style={{fontSize: '0.75em'}}>
                              {testOutput.traceback}
                            </pre>
                          </details>
                        )}
                      </Alert>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
          </>
        ) : (
          /* ========== EXISTING AGENTS TAB ========== */
          <div>
            {/* Search & Filter Bar */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <InputGroup style={{ maxWidth: '320px' }}>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search agents..."
                  value={agentSearchQuery}
                  onChange={e => setAgentSearchQuery(e.target.value)}
                />
              </InputGroup>
              <div className="d-flex flex-wrap gap-1">
                <Button
                  variant={!agentTagFilter ? 'dark' : 'outline-dark'}
                  size="sm"
                  onClick={() => setAgentTagFilter(null)}
                >All</Button>
                {AVAILABLE_TAGS.map(tag => (
                  <Button
                    key={tag.value}
                    variant={agentTagFilter === tag.value ? tag.color : `outline-${tag.color}`}
                    size="sm"
                    onClick={() => setAgentTagFilter(agentTagFilter === tag.value ? null : tag.value)}
                  >
                    {tag.label}
                  </Button>
                ))}
              </div>
              <Button variant="outline-primary" size="sm" onClick={loadExistingAgents} disabled={loadingAgents}>
                {loadingAgents ? <Spinner animation="border" size="sm" /> : '🔄'} Refresh
              </Button>
            </div>

            {loadingAgents ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading agents...</p>
              </div>
            ) : filteredExistingAgents.length === 0 ? (
              <Alert variant="info">
                {agentSearchQuery || agentTagFilter
                  ? 'No agents match your search or filter.'
                  : 'No agents found in the system.'}
              </Alert>
            ) : (
              <div className="row">
                {filteredExistingAgents.map(agent => {
                  const isExpanded = expandedAgentCards[agent.name];
                  const cachedSamples = agentSamplesCache.current[agent.name] || [];
                  return (
                  <div key={agent.name} className="col-md-6 col-lg-4 mb-3">
                    <Card className="h-100 shadow-sm border" style={{ transition: 'box-shadow 0.2s', cursor: 'pointer' }}
                      onClick={() => toggleAgentCard(agent.name)}>
                      <Card.Body className="d-flex flex-column p-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <h6 className="mb-0 text-truncate" style={{ fontSize: '0.9rem', maxWidth: '65%', opacity: agent.loadError ? 0.6 : 1 }}>
                            <FaRobot className={`me-1 ${agent.loadError ? 'text-warning' : 'text-primary'}`} />
                            {agent.display_name || agent.name}
                          </h6>
                          <div className="d-flex align-items-center gap-1">
                            {agent.loadError && (
                              <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem' }} title="Agent failed to load — dependencies may be missing">Load Error</Badge>
                            )}
                            {cachedSamples.length > 0 && (
                              <Badge bg="info" style={{ fontSize: '0.6rem' }}>{cachedSamples.length} samples</Badge>
                            )}
                            <Badge bg={agent.agentType === 'ai' ? 'primary' : 'success'} style={{ fontSize: '0.65rem' }}>
                              {agent.agentType === 'ai' ? 'AI' : 'Det'}
                            </Badge>
                          </div>
                        </div>

                        {isExpanded && (
                          <>
                            <EditableAgentMeta
                              agentName={agent.name}
                              description={agent.description}
                              tags={agent.tags}
                              onUpdate={handleUpdateAgentMeta}
                            />
                            {/* IO info */}
                            {(agent.input_schema || agent.output_schema) && (
                              <div className="mb-1 d-flex gap-1 flex-wrap" style={{ fontSize: '0.7rem' }}>
                                {agent.input_schema?.type && (
                                  <span className="px-1 rounded" style={{ background: '#e8f4fd' }}>
                                    In: {agent.input_schema.extensions?.map(e => `.${e}`).join(', ') || agent.input_schema.type}
                                  </span>
                                )}
                                {agent.output_schema?.type && (
                                  <span className="px-1 rounded" style={{ background: '#e8fde8' }}>
                                    Out: {agent.output_schema.extensions?.map(e => `.${e}`).join(', ') || agent.output_schema.type}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Sample thumbnails */}
                            {cachedSamples.length > 0 && (
                              <div className="mb-1 d-flex gap-1 flex-wrap">
                                {cachedSamples.slice(0, 6).map(s => {
                                  const isVis = ['image', 'svg'].includes(s.file_type);
                                  return isVis ? (
                                    <img key={s.id} src={`${API}/api/agent-samples/file/${agent.name}/${s.filename}`}
                                      alt={s.filename} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 3, border: '1px solid #ddd' }} />
                                  ) : (
                                    <div key={s.id} className="d-flex align-items-center justify-content-center"
                                      style={{ width: 36, height: 36, background: '#eee', borderRadius: 3, fontSize: '0.55rem', textAlign: 'center' }}>
                                      {s.file_type}
                                    </div>
                                  );
                                })}
                                {cachedSamples.length > 6 && <span className="small text-muted align-self-center">+{cachedSamples.length - 6}</span>}
                              </div>
                            )}
                            {/* Repair banner for broken agents */}
                            {agent.loadError && (
                              <Alert variant="warning" className="py-1 px-2 mb-1" style={{ fontSize: '0.72rem' }}>
                                <strong>Load error:</strong> dependencies may be missing.
                                <Button
                                  variant="warning" size="sm" className="ms-2 py-0 px-2"
                                  style={{ fontSize: '0.7rem' }}
                                  disabled={repairingAgents[agent.name] === 'loading'}
                                  onClick={(e) => { e.stopPropagation(); handleRepairAgent(agent.name); }}
                                >
                                  {repairingAgents[agent.name] === 'loading'
                                    ? <><Spinner animation="border" size="sm" className="me-1" style={{ width: 12, height: 12 }} /> Repairing...</>
                                    : repairingAgents[agent.name] === 'success'
                                      ? <><FaCheckCircle className="me-1" /> Fixed</>
                                      : <><FaWrench className="me-1" /> Repair</>}
                                </Button>
                                {repairingAgents[agent.name] && !['loading', 'success'].includes(repairingAgents[agent.name]) && (
                                  <div className="text-danger mt-1" style={{ fontSize: '0.65rem' }}>
                                    Repair status: {repairingAgents[agent.name]}
                                  </div>
                                )}
                              </Alert>
                            )}
                            {/* Action Buttons */}
                            <div className="d-flex gap-1 mt-auto pt-1 border-top" onClick={e => e.stopPropagation()}>
                              <Button variant="outline-primary" size="sm" className="flex-fill" style={{ fontSize: '0.75rem' }}
                                onClick={() => handleEditAgent(agent.name)}>
                                <FaEdit className="me-1" /> Edit
                              </Button>
                              <Button variant="outline-warning" size="sm" className="flex-fill" style={{ fontSize: '0.75rem' }}
                                onClick={() => handleRenameAgent(agent)} title="Rename agent">
                                <FaPaintBrush className="me-1" /> Rename
                              </Button>
                              <Button variant="outline-secondary" size="sm" className="flex-fill" style={{ fontSize: '0.75rem' }}
                                onClick={() => handleDuplicateAgent(agent)}>
                                <FaClone className="me-1" /> Dup
                              </Button>
                              <Button variant="outline-success" size="sm" className="flex-fill" style={{ fontSize: '0.75rem' }}
                                onClick={() => handleDuplicateToCreate(agent)} title="Copy to Create tab">
                                <FaPlus className="me-1" /> Derive
                              </Button>
                            </div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                  );
                })}
              </div>
            )}
            <div className="text-muted small mt-2">
              Showing {filteredExistingAgents.length} of {existingAgents.length} agents
            </div>
          </div>
        )}
        </Card.Body>
      </Card>

      {/* Config Editor for editing agents from Existing Agents tab */}
      <ConfigEditor
        show={showAgentConfigEditor}
        onHide={() => {
          setShowAgentConfigEditor(false);
          setEditingAgentName(null);
          loadExistingAgents(); // Refresh after editing
        }}
        type="agent"
        agentName={editingAgentName}
      />

      {/* Prompt Refinement Modal */}
      <Modal show={showRefineModal} onHide={() => setShowRefineModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaMagic className="me-2 text-primary" />
            AI-Refined Purpose
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {refinedPrompt && (
            <>
              <div className="mb-4">
                <h6 className="text-muted mb-2">Your Original Purpose:</h6>
                <div className="p-3 bg-light border rounded">{purpose}</div>
              </div>
              
              <div className="mb-4">
                <h6 className="text-success mb-2">✨ Refined Purpose:</h6>
                <div className="p-3 bg-success-subtle border border-success rounded">
                  {refinedPrompt.refined_purpose}
                </div>
              </div>
              
              {refinedPrompt.suggestions && refinedPrompt.suggestions.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-info mb-2">💡 Suggestions:</h6>
                  <ul className="mb-0">
                    {refinedPrompt.suggestions.map((suggestion, idx) => (
                      <li key={idx} className="text-muted small">{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="row">
                {refinedPrompt.suggested_name && (
                  <div className="col-md-4 mb-2">
                    <h6 className="text-muted small mb-1">Suggested Name:</h6>
                    <Badge bg="secondary">{refinedPrompt.suggested_name}</Badge>
                  </div>
                )}
                {refinedPrompt.agent_type && (
                  <div className="col-md-4 mb-2">
                    <h6 className="text-muted small mb-1">Recommended Type:</h6>
                    <Badge bg={refinedPrompt.agent_type === 'ai' ? 'primary' : 'success'}>
                      {refinedPrompt.agent_type === 'ai' ? 'AI Agent' : 'Deterministic'}
                    </Badge>
                  </div>
                )}
                {refinedPrompt.input_format && (
                  <div className="col-md-4 mb-2">
                    <h6 className="text-muted small mb-1">Input Format:</h6>
                    <code className="small">{refinedPrompt.input_format}</code>
                  </div>
                )}
              </div>
              
              {refinedPrompt.suggested_tags && refinedPrompt.suggested_tags.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-muted small mb-1">🏷️ Suggested Tags:</h6>
                  <div className="d-flex flex-wrap gap-1">
                    {refinedPrompt.suggested_tags.map((tag, idx) => {
                      const tagDef = AVAILABLE_TAGS.find(t => t.value === tag);
                      return (
                        <Badge key={idx} bg={tagDef ? tagDef.color : 'secondary'}>
                          {tagDef ? tagDef.label : tag}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Compatible Agents Section */}
              {refinedPrompt.compatible_downstream && refinedPrompt.compatible_downstream.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-muted small mb-1">🔗 Compatible Downstream Agents (can consume output):</h6>
                  <div style={{ maxHeight: '150px', overflow: 'auto' }}>
                    {refinedPrompt.compatible_downstream.slice(0, 8).map((a, idx) => {
                      const exts = a.input_schema?.extensions;
                      return (
                        <div key={idx} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: '0.85rem' }}>
                          <Badge bg={a.compatibility === 'exact' ? 'success' : 'warning'} style={{ fontSize: '0.65em', minWidth: '45px' }}>
                            {a.compatibility === 'exact' ? 'exact' : 'partial'}
                          </Badge>
                          <strong>{a.name}</strong>
                          <span className="text-muted">— {a.description?.slice(0, 50) || ''}</span>
                          {exts && <code className="small">{exts.map(e => `.${e}`).join(', ')}</code>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {refinedPrompt.compatible_upstream && refinedPrompt.compatible_upstream.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-muted small mb-1">⬆️ Compatible Upstream Agents (can produce input):</h6>
                  <div style={{ maxHeight: '120px', overflow: 'auto' }}>
                    {refinedPrompt.compatible_upstream.slice(0, 6).map((a, idx) => {
                      const exts = a.output_schema?.extensions;
                      return (
                        <div key={idx} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: '0.85rem' }}>
                          <Badge bg={a.compatibility === 'exact' ? 'success' : 'warning'} style={{ fontSize: '0.65em', minWidth: '45px' }}>
                            {a.compatibility === 'exact' ? 'exact' : 'partial'}
                          </Badge>
                          <strong>{a.name}</strong>
                          <span className="text-muted">— {a.description?.slice(0, 50) || ''}</span>
                          {exts && <code className="small">{exts.map(e => `.${e}`).join(', ')}</code>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRefineModal(false)}>
            Keep Original
          </Button>
          <Button variant="success" onClick={handleAcceptRefinedPrompt}>
            <FaCheckCircle className="me-1" />
            Use Refined Purpose
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Drawing Canvas Modal */}
      <DrawingCanvas
        show={showDrawing}
        onHide={() => setShowDrawing(false)}
        onSave={handleDrawingSave}
      />
    </div>
  );
});

export default AgentCreator;
